import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig, prefersReducedMotion } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

const cartSectionIds = () => [
  ...new Set(
    [...document.querySelectorAll('cart-items-component')]
      .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
      .filter(Boolean)
  ),
];

/**
 * POSTs to a cart endpoint, asking Shopify to render every cart section in the same response.
 * @param {string} url
 * @param {object} body
 */
async function postCart(url, body) {
  const response = await fetch(
    url,
    fetchConfig('json', {
      body: JSON.stringify({ ...body, sections: cartSectionIds().join(','), sections_url: window.location.pathname }),
    })
  );
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || response.statusText);
  return data;
}

/** @param {Record<string, string> | undefined} sections */
function itemCountFrom(sections) {
  for (const html of Object.values(sections || {})) {
    if (!html) continue;
    const count = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]');
    return count ? parseInt(count.textContent || '0', 10) : 0;
  }
  return undefined;
}

/**
 * Lets Horizon's cart components (cart items, cart icon, drawer) re-render from the returned sections.
 * @param {EventTarget} target
 * @param {any} data
 * @param {string} source
 */
function publishCart(target, data, source) {
  target.dispatchEvent(
    new CartUpdateEvent(data, source, { source, itemCount: itemCountFrom(data.sections), sections: data.sections })
  );
}

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));

/** @param {string} value */
const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

class HsCartVariantPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  get selects() {
    return /** @type {HTMLSelectElement[]} */ ([...this.querySelectorAll('select')]);
  }

  get config() {
    return JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '{}');
  }

  /** @param {Event} event */
  #onChange = async (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const { variants = [], properties, sellingPlan } = this.config;
    const selected = this.selects.map((element) => element.value);
    const changedIndex = Number(select.dataset.optionIndex);
    const currentId = Number(this.dataset.variantId);
    const current = variants.find((/** @type {any} */ variant) => variant.id === currentId);

    const matches = (/** @type {any} */ variant) =>
      variant.options.reduce((/** @type {number} */ score, /** @type {string} */ value, /** @type {number} */ i) => score + (value === selected[i] ? 1 : 0), 0);
    const target =
      variants.find((/** @type {any} */ variant) => variant.available && matches(variant) === selected.length) ||
      variants
        .filter((/** @type {any} */ variant) => variant.available && variant.options[changedIndex] === selected[changedIndex])
        .sort((/** @type {any} */ a, /** @type {any} */ b) => matches(b) - matches(a))[0];

    if (!target) {
      if (current) this.selects.forEach((element, i) => (element.value = current.options[i]));
      this.#showError(this.dataset.soldOut || '');
      return;
    }
    if (target.id === currentId) return;

    const cartItems = this.closest('cart-items-component');
    cartItems?.classList.add('cart-items-disabled');
    this.selects.forEach((element) => (element.disabled = true));

    /** @param {number} id */
    const lineFor = (id) => {
      /** @type {Record<string, any>} */
      const item = { id, quantity: Number(this.dataset.quantity) || 1 };
      if (properties && !Array.isArray(properties) && Object.keys(properties).length) item.properties = properties;
      if (sellingPlan) item.selling_plan = sellingPlan;
      return item;
    };

    // Line keys change whenever discounts are reallocated, so the old line is removed before the new variant is added.
    let removed = false;
    try {
      await postCart(Theme.routes.cart_change_url, { id: this.dataset.lineKey, quantity: 0 });
      removed = true;
      publishCart(this, await postCart(Theme.routes.cart_add_url, { items: [lineFor(target.id)] }), 'hs-cart-variant-picker');
    } catch (error) {
      if (current) this.selects.forEach((element, i) => (element.value = current.options[i]));
      this.#showError(error instanceof Error ? error.message : String(error));
      const restored = removed
        ? await postCart(Theme.routes.cart_add_url, { items: [lineFor(currentId)] }).catch(() => null)
        : null;
      if (restored) publishCart(this, restored, 'hs-cart-variant-picker');
    } finally {
      cartItems?.classList.remove('cart-items-disabled');
      this.selects.forEach((element) => (element.disabled = false));
    }
  };

  /** @param {string} message */
  #showError(message) {
    const error = this.querySelector('.hs-cart-variants__error');
    if (!error) return;
    error.textContent = message;
    error.classList.remove('hidden');
  }
}

class HsCartDiscount extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.#apply);
    this.addEventListener('click', this.#remove);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#apply);
    this.removeEventListener('click', this.#remove);
  }

  get codes() {
    return [...this.querySelectorAll('[data-code]')].map((element) => /** @type {HTMLElement} */ (element).dataset.code || '');
  }

  /** @param {Event} event */
  #apply = async (event) => {
    event.preventDefault();
    const input = this.querySelector('input[name="discount"]');
    if (!(input instanceof HTMLInputElement)) return;
    const code = input.value.trim();
    if (!code || this.codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) return;

    this.#toggleError(false);
    this.setAttribute('aria-busy', 'true');
    try {
      const data = await postCart(Theme.routes.cart_update_url, { discount: [...this.codes, code].join(',') });
      const applied = (data.discount_codes || []).find(
        (/** @type {{code: string, applicable: boolean}} */ discount) => discount.code.toLowerCase() === code.toLowerCase()
      );
      if (!applied?.applicable) {
        this.#toggleError(true);
        return;
      }
      publishCart(this, data, 'hs-cart-discount');
    } catch {
      this.#toggleError(true);
    } finally {
      this.removeAttribute('aria-busy');
    }
  };

  /** @param {Event} event */
  #remove = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('.hs-cart-discount__remove') : null;
    const code = button?.closest('[data-code]')?.getAttribute('data-code');
    if (!code) return;

    const data = await postCart(Theme.routes.cart_update_url, {
      discount: this.codes.filter((existing) => existing !== code).join(','),
    });
    publishCart(this, data, 'hs-cart-discount');
  };

  /** @param {boolean} show */
  #toggleError(show) {
    this.querySelector('.hs-cart-discount__error')?.classList.toggle('hidden', !show);
  }
}

class HsCartCarousel extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.track?.addEventListener('scroll', this.refresh, { passive: true });
    requestAnimationFrame(this.refresh);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.track?.removeEventListener('scroll', this.refresh);
  }

  get track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.hs-cart-carousel__track'));
  }

  get index() {
    const { track } = this;
    return track && track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
  }

  refresh = () => {
    const { track } = this;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const [previous, next] = this.querySelectorAll('.hs-cart-carousel__arrow');
    if (previous instanceof HTMLButtonElement) previous.disabled = track.scrollLeft <= 1;
    if (next instanceof HTMLButtonElement) next.disabled = track.scrollLeft >= maxScroll - 1;

    this.querySelectorAll('.hs-cart-carousel__dot').forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));

    this.querySelectorAll('.hs-cart-testimonial').forEach((slide) => {
      const text = slide.querySelector('.hs-cart-testimonial__text');
      const more = slide.querySelector('.hs-cart-testimonial__more');
      if (!text || !more || slide.classList.contains('hs-cart-testimonial--expanded')) return;
      more.classList.toggle('hidden', text.scrollHeight <= text.clientHeight + 1);
    });
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const { track } = this;
    if (!track || !(event.target instanceof Element)) return;
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';

    const arrow = event.target.closest('.hs-cart-carousel__arrow');
    if (arrow instanceof HTMLElement) {
      track.scrollBy({ left: Number(arrow.dataset.direction) * track.clientWidth, behavior });
      return;
    }

    const dot = event.target.closest('.hs-cart-carousel__dot');
    if (dot instanceof HTMLElement) {
      track.scrollTo({ left: Number(dot.dataset.index) * track.clientWidth, behavior });
      return;
    }

    const more = event.target.closest('.hs-cart-testimonial__more');
    if (more instanceof HTMLElement) {
      const slide = more.closest('.hs-cart-testimonial');
      const expanded = !slide?.classList.contains('hs-cart-testimonial--expanded');
      slide?.classList.toggle('hs-cart-testimonial--expanded', expanded);
      more.setAttribute('aria-expanded', String(expanded));
      const label = more.dataset.less;
      more.dataset.less = more.textContent?.trim() || '';
      more.textContent = label || '';
    }
  };
}

/**
 * Product dialog used for the post-add upsell offer ("popup" mode) and for "Choose options" in the cart upsell ("quick" mode).
 */
class HsCartDialog extends HTMLElement {
  /** @type {Map<string, Promise<any>>} */
  #products = new Map();
  /** @type {any} */
  #product = null;
  /** @type {'popup' | 'quick'} */
  #mode = 'quick';

  connectedCallback() {
    this.config = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '{}');
    this.dialog = /** @type {HTMLDialogElement} */ (this.querySelector('dialog'));
    this.body = /** @type {HTMLElement} */ (this.querySelector('.hs-cart-dialog__body'));

    this.dialog.addEventListener('click', this.#onDialogClick);
    this.dialog.addEventListener('close', this.#onClose);
    this.dialog.addEventListener('keydown', this.#onKeydown);
    this.body.addEventListener('change', this.#sync);
    this.body.addEventListener('submit', this.#onSubmit);
    document.addEventListener('click', this.#onQuickAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onQuickAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /** @param {string} handle */
  #fetchProduct(handle) {
    if (!this.#products.has(handle)) {
      const url = `${String(this.config.root || '/').replace(/\/$/, '')}/products/${encodeURIComponent(handle)}.js`;
      this.#products.set(
        handle,
        fetch(url).then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          return response.json();
        })
      );
    }
    return /** @type {Promise<any>} */ (this.#products.get(handle));
  }

  /** @param {Event} event */
  #onQuickAdd = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-hs-quick-add]') : null;
    if (!(button instanceof HTMLElement)) return;
    event.preventDefault();

    button.setAttribute('aria-busy', 'true');
    try {
      await this.open(button.dataset.hsQuickAdd || '', 'quick');
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  /** @param {Event} event */
  #onCartUpdate = async (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail?.data;
    const popup = this.config.popup;
    if (!popup?.offer || detail?.source !== 'product-form-component' || detail.didError) return;

    const productId = Number(detail.productId);
    if (productId === popup.offerId || !popup.triggers.includes(productId)) return;

    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
    if (cart.items.some((/** @type {any} */ item) => item.product_id === popup.offerId)) return;

    await this.open(popup.offer, 'popup');
  };

  /**
   * @param {string} handle
   * @param {'popup' | 'quick'} mode
   */
  async open(handle, mode) {
    this.#product = await this.#fetchProduct(handle);
    this.#mode = mode;
    this.body.innerHTML = this.#render(this.#product);
    this.#sync();

    if (mode === 'popup') {
      await nextFrame();
      await nextFrame();
      const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
      if (drawer?.refs?.dialog?.open) await drawer.closeDialog();
    }
    if (!this.dialog.open) this.dialog.showModal();
  }

  close() {
    this.dialog.close();
  }

  #onClose = () => {
    if (this.#mode !== 'popup') return;
    const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    } else if (window.location.pathname !== Theme.routes.cart_url) {
      window.location.href = Theme.routes.cart_url;
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  };

  /** @param {MouseEvent} event */
  #onDialogClick = (event) => {
    if (event.target === this.dialog || (event.target instanceof Element && event.target.closest('[data-hs-dialog-close]'))) {
      this.close();
    }
  };

  /** @param {any} product */
  #render(product) {
    const popup = this.#mode === 'popup' ? this.config.popup : null;
    const options = (product.options || []).filter(
      (/** @type {any} */ option) => !(product.variants.length === 1 && option.values.length === 1 && option.values[0] === 'Default Title')
    );
    const first = product.variants.find((/** @type {any} */ variant) => variant.available) || product.variants[0];
    const gift = popup ? this.querySelector('template[data-icon="gift"]')?.innerHTML || '' : '';

    const optionsHtml = options
      .map(
        (/** @type {any} */ option, /** @type {number} */ index) => `
          <fieldset class="hs-cart-dialog__option">
            <legend>${escapeHtml(option.name)}: <span data-option-label="${index}">${escapeHtml(first.options[index])}</span></legend>
            <div class="hs-cart-dialog__values">
              ${option.values
                .map(
                  (/** @type {string} */ value) => `
                  <label class="hs-cart-dialog__value">
                    <input type="radio" name="option-${index}" value="${escapeHtml(value)}" ${value === first.options[index] ? 'checked' : ''}>
                    <span>${escapeHtml(value)}</span>
                  </label>`
                )
                .join('')}
            </div>
          </fieldset>`
      )
      .join('');

    return `
      ${popup ? `<p class="hs-cart-dialog__title" id="HsCartDialogTitle">${escapeHtml(popup.title)}</p>` : ''}
      <div class="hs-cart-dialog__grid">
        <div class="hs-cart-dialog__media"><img alt="${escapeHtml(product.title)}" width="600" height="600"></div>
        <form class="hs-cart-dialog__form" novalidate>
          ${
            popup
              ? `<p class="hs-cart-dialog__heading">${escapeHtml(popup.heading)}</p>
                 <div class="hs-cart-dialog__text">${popup.text || ''}</div>`
              : `<a class="hs-cart-dialog__product-title" id="HsCartDialogTitle" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
                 <p class="hs-cart-dialog__text" data-price></p>`
          }
          ${optionsHtml}
          <button type="submit" class="button hs-cart-dialog__submit">${gift}<span data-submit-label></span></button>
          <p class="hs-cart-dialog__error hidden" role="alert"></p>
        </form>
      </div>
      ${
        popup
          ? `<div class="hs-cart-dialog__footer">
               <button type="button" data-hs-dialog-close>${escapeHtml(popup.decline)}</button>
               <a href="${escapeHtml(popup.checkoutUrl)}">${escapeHtml(popup.checkout)}</a>
             </div>`
          : ''
      }`;
  }

  get selectedVariant() {
    const product = this.#product;
    if (!product) return null;
    const selected = [...this.body.querySelectorAll('fieldset')].map(
      (fieldset) => /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'))?.value
    );
    return product.variants.find((/** @type {any} */ variant) => selected.every((value, i) => value === variant.options[i])) || null;
  }

  #sync = () => {
    const product = this.#product;
    const variant = this.selectedVariant;
    const selected = [...this.body.querySelectorAll('fieldset')].map(
      (fieldset) => /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'))?.value
    );

    this.body.querySelectorAll('fieldset').forEach((fieldset, index) => {
      const label = fieldset.querySelector(`[data-option-label="${index}"]`);
      if (label) label.textContent = selected[index] || '';
      fieldset.querySelectorAll('input').forEach((input) => {
        const available = product.variants.some(
          (/** @type {any} */ candidate) =>
            candidate.available &&
            candidate.options[index] === input.value &&
            selected.every((value, i) => i === index || value === candidate.options[i])
        );
        input.closest('.hs-cart-dialog__value')?.classList.toggle('hs-cart-dialog__value--unavailable', !available);
      });
    });

    const image = this.body.querySelector('.hs-cart-dialog__media img');
    const src = variant?.featured_image?.src || product.featured_image;
    if (image instanceof HTMLImageElement && src) {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '600');
      image.src = url.toString();
    }

    const price = this.body.querySelector('[data-price]');
    if (price && variant) price.innerHTML = formatMoney(variant.price, this.config.moneyFormat, this.config.currency);

    const submit = this.body.querySelector('.hs-cart-dialog__submit');
    const submitLabel = this.body.querySelector('[data-submit-label]');
    const available = Boolean(variant?.available);
    if (submit instanceof HTMLButtonElement) submit.disabled = !available;
    if (submitLabel) {
      const label = this.#mode === 'popup' ? this.config.popup.button : this.config.addLabel;
      submitLabel.textContent = available ? label : this.config.soldOutLabel;
    }
  };

  /** @param {Event} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const variant = this.selectedVariant;
    const submit = this.body.querySelector('.hs-cart-dialog__submit');
    const error = this.body.querySelector('.hs-cart-dialog__error');
    if (!variant?.available || !(submit instanceof HTMLButtonElement)) return;

    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    error?.classList.add('hidden');
    try {
      const data = await postCart(Theme.routes.cart_add_url, { items: [{ id: variant.id, quantity: 1 }] });
      publishCart(this, data, 'hs-cart-dialog');
      this.close();
    } catch (err) {
      if (error) {
        error.textContent = err instanceof Error ? err.message : String(err);
        error.classList.remove('hidden');
      }
    } finally {
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
    }
  };
}

document.addEventListener(ThemeEvents.cartUpdate, () => {
  requestAnimationFrame(() => {
    document.querySelectorAll('hs-cart-carousel').forEach((carousel) => /** @type {any} */ (carousel).refresh?.());
  });
});

if (!customElements.get('hs-cart-variant-picker')) customElements.define('hs-cart-variant-picker', HsCartVariantPicker);
if (!customElements.get('hs-cart-discount')) customElements.define('hs-cart-discount', HsCartDiscount);
if (!customElements.get('hs-cart-carousel')) customElements.define('hs-cart-carousel', HsCartCarousel);
if (!customElements.get('hs-cart-dialog')) customElements.define('hs-cart-dialog', HsCartDialog);
